import { z } from 'zod';

/**
 * The live-update contract for `GET /api/events`.
 *
 * INV-4: a payload carries OBJECT METADATA IDS ONLY. Never a record id, never a
 * value. A client learns "something in object X changed" and refetches through the
 * permission-checked read path; anything finer would leak the existence of rows the
 * listener may not be allowed to see, and would leak them over a channel that has no
 * permission check of its own.
 *
 * Four event types:
 *  - `hello`      once, on connect: the metadata version the stream starts from, so a
 *                 client that reconnects can tell whether it missed a schema change.
 *  - `invalidate` record data in these objects changed.
 *  - `metadata`   the metadata version moved; these objects were touched. A client
 *                 refetches its metadata before it refetches records, because its
 *                 field ids may have changed meaning.
 *  - `pending`    pending changes (INV-10) were created or decided. IDS ONLY, by the
 *                 same rule as the others: a pending-change id is an opaque handle,
 *                 and the approval screen refetches through the permission-checked
 *                 read path. No record id, no proposed value, no proposer.
 *
 * Heartbeat comments (`: heartbeat`) go out every 25 s. They are not events; they
 * exist because an idle stream behind a reverse proxy is indistinguishable from a
 * dead one until something is written to it.
 */

export const SSE_EVENT_TYPES = ['hello', 'invalidate', 'metadata', 'pending'] as const;
export type SseEventType = (typeof SSE_EVENT_TYPES)[number];

export const SSE_HEARTBEAT_INTERVAL_MS = 25_000;

export const helloEventSchema = z.strictObject({
  metadataVersion: z.number(),
});

export const invalidateEventSchema = z.strictObject({
  objectIds: z.array(z.uuid()),
});

export const metadataEventSchema = z.strictObject({
  version: z.number(),
  objectIds: z.array(z.uuid()),
});

export const pendingEventSchema = z.strictObject({
  /** Pending-change ids that were created or decided. Opaque handles, nothing else. */
  ids: z.array(z.uuid()),
});

export type HelloEvent = z.infer<typeof helloEventSchema>;
export type InvalidateEvent = z.infer<typeof invalidateEventSchema>;
export type MetadataEvent = z.infer<typeof metadataEventSchema>;
export type PendingEvent = z.infer<typeof pendingEventSchema>;
